import { useEffect, useState } from "react";
import { UnisphereRuntimeLoader } from "@unisphere/runtime-react";
import { UnisphereRuntimeBaseType } from "@unisphere/runtime";

type SettingsTByRuntimeT<RuntimeT extends UnisphereRuntimeBaseType<any>> =
    RuntimeT extends UnisphereRuntimeBaseType<infer SettingT> ? SettingT : never;

export interface UnisphereRuntimeVisualInBodyProps<RuntimeT extends UnisphereRuntimeBaseType<any>>
    extends Pick<RuntimeT, "widgetName" | "runtimeName"> {
    runtimeSettings: SettingsTByRuntimeT<RuntimeT>;
    visualType: string;
    onRuntimeAvailable?: (runtime: RuntimeT) => void;
}

/**
 * Component to render unisphere runtime visual in the "body" tag
 * (the OOTB UnisphereRuntimeVisual component doesn't support it).
 */
export const UnisphereRuntimeVisualInBody = <RuntimeT extends UnisphereRuntimeBaseType<any>>({
    widgetName,
    runtimeName,
    runtimeSettings,
    visualType,
    onRuntimeAvailable,
}: UnisphereRuntimeVisualInBodyProps<RuntimeT>) => {
    const [runtime, setRuntime] = useState<RuntimeT>();

    const handleLoad = (runtime: RuntimeT) => {
        setRuntime(runtime);
        onRuntimeAvailable?.(runtime);
    };

    useEffect(() => {
        if (!runtime) {
            return;
        }

        const visual = runtime.mountVisual({
            type: visualType,
            target: { target: "body" },
            settings: {},
        });

        return () => {
            visual.unsubscribe();
        };
    }, [runtime, visualType]);

    return (
        <UnisphereRuntimeLoader
            widgetName={widgetName}
            runtimeName={runtimeName}
            runtimeSettings={runtimeSettings}
            onRuntimeAvailable={(runtime) => handleLoad(runtime as RuntimeT)}
        />
    );
};
